/**
 * 배포 다이얼로그 오른쪽 — 이번 배포에 쓰이는 현장 설정 파일.
 * 왼쪽 서비스 선택과 나란히 두어, 배포안함으로 뺀 서비스의 파일은 흐리게 보인다.
 * 고치는 곳은 배포 화면의 '현장 설정 파일' 패널이다(여기는 보기만).
 */
import React from 'react';
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { SiteConfigService } from '../../../services/SiteConfigService';
import { commitTitle, formatConfigSize, siteConfigTabLabel } from '../../../pages/ProjectDashboardPage/sections/siteConfigFiles';
import { deployConfigFiles } from '../hooks/deployConfigFiles';
import type { SelectionMap } from '../hooks/useDeployServiceSelection';
import type { DeployTabType } from '../types';

interface Props {
  projectId: number | null;
  target: DeployTabType;
  selection: SelectionMap;
}

const DeployConfigFiles: React.FC<Props> = ({ projectId, target, selection }) => {
  // 배포 화면의 설정 파일 패널과 같은 query key — 캐시를 같이 쓴다.
  const { data, isLoading, error } = useQuery({
    queryKey: ['site-config', projectId],
    queryFn: () => SiteConfigService.list(projectId as number),
    enabled: projectId != null,
    staleTime: 60_000,
  });
  const sites = data?.sites ?? [];

  return (
    <Box data-testid="deploy-config-files">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        설정 파일 ({target === 'remote' ? '현장 통합서버' : '사내 서버 + 현장'})
      </Typography>
      {isLoading && <CircularProgress size={18} />}
      {error && <Alert severity="warning">설정 파일 목록을 읽지 못했습니다.</Alert>}
      <Stack spacing={1.5}>
        {sites.map((site) => {
          const files = deployConfigFiles(site.files, target, selection);
          return (
            <Box key={site.code || site.room}>
              {sites.length > 1 && (
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{siteConfigTabLabel(site)}</Typography>
              )}
              {site.path && (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                  {site.path}
                </Typography>
              )}
              {site.lastCommit && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  마지막 변경 {new Date(site.lastCommit.date).toLocaleString('ko-KR')} ·{' '}
                  <Link href={site.lastCommit.html_url} target="_blank" rel="noopener noreferrer">
                    {commitTitle(site.lastCommit.message)}
                  </Link>
                </Typography>
              )}
              {site.problem && (
                <Alert severity="warning" sx={{ mt: 0.5 }}>
                  {site.problem}
                </Alert>
              )}
              <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
                {files.map((f) => (
                  <Box
                    component="li"
                    key={f.name}
                    data-active={f.active}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      opacity: f.active ? 1 : 0.45,
                    }}
                  >
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" color={f.exists ? 'text.secondary' : 'error'} sx={{ flex: 'none' }}>
                        {formatConfigSize(f)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                      {f.services.length > 0 ? (
                        f.services.map((s) => <Chip key={s} label={s} size="small" variant="outlined" />)
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {f.label} — 이 배포 목록 밖
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        흐린 파일은 배포안함으로 뺀 서비스의 설정입니다. 고치려면 배포 화면의 '현장 설정 파일'에서.
      </Typography>
    </Box>
  );
};

export default DeployConfigFiles;
